<template>
    <teleport to="body">
        <div
            v-if="open"
            class="fixed inset-0 z-[600] flex items-center justify-center bg-black/30 p-4"
            @click.self="$emit('close')"
        >
            <div class="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
                <h3 class="mb-4 text-lg font-medium text-gray-800">
                    {{ t("vue.ai_chat_email_dialog_title") }}
                </h3>

                <p v-if="loadingMembers" class="text-sm text-gray-500">
                    {{ t("vue.loading") }}
                </p>
                <div
                    v-else
                    class="max-h-64 space-y-0.5 overflow-y-auto rounded-lg border border-gray-100 p-1"
                >
                    <label
                        v-for="member in members"
                        :key="member.id"
                        class="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 transition hover:bg-gray-50"
                    >
                        <input
                            v-model="selectedIds"
                            type="checkbox"
                            :value="member.id"
                            class="h-4 w-4 rounded border-gray-300 text-gray-800 focus:ring-gray-500"
                        />
                        <span class="text-sm text-gray-700">
                            {{ member.name }}
                            <span v-if="member.is_self" class="text-gray-400">
                                {{ t("vue.ai_chat_email_you") }}
                            </span>
                        </span>
                    </label>
                </div>

                <div class="mt-6 flex justify-end gap-3">
                    <AtomicButton
                        color="colorless"
                        size="sm"
                        @click="$emit('close')"
                    >
                        {{ t("vue.cancel") }}
                    </AtomicButton>
                    <AtomicButton
                        color="yellow"
                        size="sm"
                        :disabled="sending || selectedIds.length === 0"
                        @click="$emit('send', { recipientIds: selectedIds })"
                    >
                        {{ t("vue.ai_chat_email_send") }}
                    </AtomicButton>
                </div>
            </div>
        </div>
    </teleport>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";
import route from "ziggy-js";
import { create } from "@/composables/useNotifications";
import { useTranslations } from "@/composables/useTranslations";

interface IEmailRecipient {
    id: number;
    name: string;
    is_self: boolean;
}

const props = withDefaults(
    defineProps<{
        open: boolean;
        sending?: boolean;
    }>(),
    { sending: false },
);

defineEmits<{
    (event: "close"): void;
    (event: "send", payload: { recipientIds: number[] }): void;
}>();

const { t } = useTranslations();

const loadingMembers = ref(false);
const members = ref<IEmailRecipient[]>([]);
const selectedIds = ref<number[]>([]);

// Recipients are the current team's members (loaded lazily on first open). The
// sender is always present and pre-selected, so "email to myself" is one click.
const loadMembers = async (): Promise<void> => {
    if (members.value.length > 0) {
        return;
    }
    loadingMembers.value = true;
    try {
        const { data } = await axios.get(route("ai-chat.api.team-members"));
        members.value = data.members ?? [];
    } catch {
        create({ title: t("vue.ai_chat_email_failed"), type: "error" });
    } finally {
        loadingMembers.value = false;
    }
};

watch(
    () => props.open,
    async (open) => {
        if (!open) {
            return;
        }
        await loadMembers();
        if (selectedIds.value.length === 0) {
            selectedIds.value = members.value
                .filter((member) => member.is_self)
                .map((member) => member.id);
        }
    },
);
</script>
